import { useEffect, useRef, useState } from 'react';
import { TrendingUp, Code, Briefcase, CreditCard, ChevronRight, Quote } from 'lucide-react';

const personas = [
  {
    icon: TrendingUp,
    role: 'SVP of Payments',
    title: 'Own Your Payment Stack. Capture Your Revenue.',
    pain: 'Tired of paying Zelle fees on your own customers\' transactions?',
    description: 'NexGenPay gives you full control of the digital payment experience — white-label, bank-owned, and revenue-generating on all four rails. Stop subsidizing third-party fintechs and start earning on every FedNow, RTP, and stablecoin transaction your customers make.',
    benefits: [
      'Own the payment UX end-to-end',
      'Revenue on every rail — FedNow, RTP, Stablecoin',
      'Full brand control, zero third-party attribution',
      'Smart Router picks the optimal rail automatically',
    ],
    quote: '"We processed $2B through Zelle last year and earned $0. That ends now."',
    color: '#00FF00',
    bg: 'rgba(0,255,0,0.03)',
  },
  {
    icon: Code,
    role: 'Chief Technology Officer',
    title: 'Four Rails. One Integration. Deploy in Weeks.',
    pain: 'Need FedNow + RTP + Stablecoin without managing four separate vendor contracts?',
    description: 'NexGenPay\'s Smart Router replaces your entire multi-vendor stack with a single API. Pre-built connectors for Fiserv, Jack Henry, FIS, and Finastra. BSA/AML compliance architecture baked in. GENIUS Act-ready. 5/5 unit tests passing.',
    benefits: [
      'Single API — replaces Payfinia + Stablecore stack',
      'Fiserv, Jack Henry, FIS, Finastra connectors',
      'Examiner-ready audit logs on every decision',
      '<100ms Smart Router, GENIUS Act compliant',
    ],
    quote: '"Finally a platform that speaks our core banking language out of the box."',
    color: '#8B5CF6',
    bg: 'rgba(139,92,246,0.03)',
  },
  {
    icon: Briefcase,
    role: 'CEO / President',
    title: 'Turn Payments Into Your Biggest Revenue Driver.',
    pain: 'Looking for new non-interest income in a compressed margin environment?',
    description: 'NexGenPay transforms your payment infrastructure from a cost center into a profit engine. Gross margins 70%+ at steady state. Transaction fees, platform revenue, and 4.25% APY tokenized deposit products — all flowing directly to your institution.',
    benefits: [
      '70%+ gross margins at steady state',
      'New non-interest income across four rails',
      'First-mover advantage in your market',
      '$240M ARR target across platform by 2030',
    ],
    quote: '"This isn\'t a technology project — it\'s a revenue strategy."',
    color: '#F59E0B',
    bg: 'rgba(245,158,11,0.03)',
  },
  {
    icon: CreditCard,
    role: 'Credit Union CIO',
    title: 'The Four-Rail Platform Built for Your Members.',
    pain: 'Need to future-proof your CU against big-bank digital wallets and neo-banks?',
    description: 'NexGenPay\'s white-label platform is purpose-built for credit unions ($500M–$5B). Your brand, your member data, your fee income. Stablecoin and tokenized deposit products give your members competitive 4.25% APY — delivered under your brand, not a fintech\'s.',
    benefits: [
      'CU-branded wallet — zero fintech attribution',
      'Stablecoin + Tokenized Deposits (4.25% APY)',
      'Member onboarding via SMS — $0 CAC',
      'Compliance-first: BSA/AML + Travel Rule built-in',
    ],
    quote: '"Our members deserve the same digital experience as Chase customers — under our brand."',
    color: '#06B6D4',
    bg: 'rgba(6,182,212,0.03)',
  },
];

export default function AudienceSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="audience"
      data-testid="audience-section"
      ref={sectionRef}
      className="relative section-padding"
      style={{
        scrollMarginTop: '80px',
        background: 'linear-gradient(180deg, #000 0%, #050505 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease',
          }}>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="green-line" />
            <span className="text-[#00FF00] font-mono text-xs uppercase tracking-widest">Built For</span>
            <div className="green-line" />
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-white leading-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}>
            For the Decision-Makers Who
            <br />
            <span className="text-[#00FF00]">Drive Banking Forward</span>
          </h2>
          <p className="text-[#A3A3A3] text-base mt-4 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Built for community banks, regional banks, and credit unions. Sub-$1B asset banks, $500M–$5B credit unions, and regional FIs ready to own their payment future.
          </p>
        </div>

        {/* Tab selector */}
        <div className="flex gap-0 mb-8 border border-[#1A1A1A] w-fit mx-auto"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.2s',
          }}>
          {personas.map((p, i) => {
            const Icon = p.icon;
            return (
              <button
                key={p.role}
                data-testid={`audience-tab-${i}`}
                onClick={() => setActive(i)}
                className="flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-widest transition-all duration-300"
                style={{
                  fontFamily: 'Unbounded, sans-serif',
                  background: active === i ? p.color : 'transparent',
                  color: active === i ? '#000' : '#A3A3A3',
                  borderRight: i < 3 ? '1px solid #1A1A1A' : 'none',
                }}
              >
                <Icon size={14} />
                <span className="hidden sm:inline">{p.role}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Active card */}
        {personas.map((p, i) => {
          const Icon = p.icon;
          return (
            <div
              key={p.role}
              data-testid={`audience-card-${i}`}
              className="grid grid-cols-1 lg:grid-cols-5 gap-0 border"
              style={{
                display: active === i ? 'grid' : 'none',
                background: p.bg,
                borderColor: p.color,
                borderOpacity: 0.4,
                border: `1px solid ${p.color}40`,
                opacity: revealed ? 1 : 0,
                transform: revealed ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 0.5s ease 0.3s',
              }}
            >
              {/* Left panel */}
              <div className="lg:col-span-2 p-8 border-b lg:border-b-0 lg:border-r"
                style={{ borderColor: `${p.color}20`, background: `${p.color}05` }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 border" style={{ background: `${p.color}15`, borderColor: `${p.color}40` }}>
                    <Icon size={24} color={p.color} />
                  </div>
                  <div>
                    <div className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: p.color }}>
                      Speaking to
                    </div>
                    <div className="text-white font-bold text-sm uppercase tracking-wide"
                      style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '12px' }}>
                      {p.role}
                    </div>
                  </div>
                </div>
                <p className="text-[#A3A3A3] text-sm italic mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {p.pain}
                </p>
                <div className="flex flex-col gap-2">
                  {p.benefits.map((b) => (
                    <div key={b} className="flex items-center gap-2">
                      <ChevronRight size={12} color={p.color} />
                      <span className="text-white text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{b}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right panel */}
              <div className="lg:col-span-3 p-8 flex flex-col justify-between">
                <div>
                  <h3 className="text-white font-black mb-4 uppercase leading-tight"
                    style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '15px' }}>
                    {p.title}
                  </h3>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed mb-8" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {p.description}
                  </p>
                </div>

                <div>
                  <div className="border-l-2 pl-4 mb-6" style={{ borderColor: p.color }}>
                    <Quote size={14} color={p.color} className="mb-2 opacity-60" />
                    <p className="text-white text-sm italic leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {p.quote}
                    </p>
                  </div>
                  <button
                    onClick={() => document.querySelector('#demo')?.scrollIntoView({ behavior: 'smooth' })}
                    data-testid={`audience-cta-${i}`}
                    className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest transition-all duration-300 px-6 py-3"
                    style={{
                      fontFamily: 'Unbounded, sans-serif',
                      background: p.color,
                      color: '#000',
                    }}
                  >
                    Request a Demo <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
